import { inchesToPixels, mmToPixels } from "./paper.utils";

export enum PaperSize {
  Letter = "letter",
  Legal = "legal",
  Tabloid = "tabloid",
  Statement = "statement",
  Executive = "executive",
  Folio = "folio",
  A3 = "a3",
  A4 = "a4",
  A5 = "a5",
  B4 = "b4",
  B5 = "b5",
  IndexCard = "indexCard",
  BusinessCard = "businessCard",
}

export const paperSizeOptions = [
  {
    label: `Letter (8.5” x 11”)`,
    value: PaperSize.Letter,
  },
  {
    label: `Legal (8.5” x 14”)`,
    value: PaperSize.Legal,
  },
  {
    label: `Tabloid (11” x 17”)`,
    value: PaperSize.Tabloid,
  },
  {
    label: `Statement (5.5” x 8.5”)`,
    value: PaperSize.Statement,
  },
  {
    label: `Executive (7.25” x 10.5”)`,
    value: PaperSize.Executive,
  },
  {
    label: `Folio (8.5” x 13”)`,
    value: PaperSize.Folio,
  },
  {
    label: `A3 (297mm x 420mm)`,
    value: PaperSize.A3,
  },
  {
    label: `A4 (210mm x 297mm)`,
    value: PaperSize.A4,
  },
  {
    label: `A5 (148mm x 210mm)`,
    value: PaperSize.A5,
  },
  {
    label: `B4 (250mm x 353mm)`,
    value: PaperSize.B4,
  },
  {
    label: `B5 (176mm x 250mm)`,
    value: PaperSize.B5,
  },
  {
    value: PaperSize.IndexCard,
    label: "Index Card (545px x 327px)",
  },
  {
    value: PaperSize.BusinessCard,
    label: "Business Card (381px x 218px)",
  },
];

export type PaperSizeTypes = PaperSize;

export enum PaperOrientation {
  Portrait = "portrait",
  Landscape = "landscape",
}

export const PaperOrientationOptions = [
  {
    value: PaperOrientation.Portrait,
    label: "Portrait",
  },
  {
    value: PaperOrientation.Landscape,
    label: "Landscape",
  },
];

export type PaperOrientationTypes = PaperOrientation;

export enum BackgroundStyle {
  FitCanvas = "Fit Canvas",
  DisplayAsUploaded = "Display as uploaded",
}

export const backgroundStyleOptions = [
  {
    value: BackgroundStyle.FitCanvas,
    label: "Fit Canvas",
  },
  {
    value: BackgroundStyle.DisplayAsUploaded,
    label: "Display as uploaded",
  },
];

export type BackgroundStyleTypes = BackgroundStyle;

/**
 * The sheet's dimensions only. Orientation is the user's choice and is stored
 * on the design, so it deliberately does not belong to a paper size.
 */
export interface PaperSizeConfig {
  width: number;
  height: number;
}

/**
 * Lays a sheet out along the given orientation. Every producer of page
 * dimensions must go through this: a page whose width/height disagrees with its
 * stored orientation exports clipped, because jsPDF re-shapes the format it is
 * given to match the orientation it is told.
 */
export const getOrientedSize = (
  { width, height }: PaperSizeConfig,
  orientation: PaperOrientation,
): PaperSizeConfig => {
  const longerSide = Math.max(width, height);
  const shorterSide = Math.min(width, height);

  return orientation === PaperOrientation.Landscape
    ? { width: longerSide, height: shorterSide }
    : { width: shorterSide, height: longerSide };
};

export const paperSizeConfigs: Record<PaperSize, PaperSizeConfig> = {
  [PaperSize.Letter]: {
    width: inchesToPixels(8.5),
    height: inchesToPixels(11),
  },
  [PaperSize.Legal]: {
    width: inchesToPixels(8.5),
    height: inchesToPixels(14),
  },
  [PaperSize.Tabloid]: {
    width: inchesToPixels(11),
    height: inchesToPixels(17),
  },
  [PaperSize.Statement]: {
    width: inchesToPixels(5.5),
    height: inchesToPixels(8.5),
  },
  [PaperSize.Executive]: {
    width: inchesToPixels(7.25),
    height: inchesToPixels(10.5),
  },
  [PaperSize.Folio]: {
    width: inchesToPixels(8.5),
    height: inchesToPixels(13),
  },
  [PaperSize.A3]: {
    width: mmToPixels(297),
    height: mmToPixels(420),
  },
  [PaperSize.A4]: {
    width: mmToPixels(210),
    height: mmToPixels(297),
  },
  [PaperSize.A5]: {
    width: mmToPixels(148),
    height: mmToPixels(210),
  },
  [PaperSize.B4]: {
    width: mmToPixels(250),
    height: mmToPixels(353),
  },
  [PaperSize.B5]: {
    width: mmToPixels(176),
    height: mmToPixels(250),
  },
  [PaperSize.IndexCard]: {
    width: inchesToPixels(3),
    height: inchesToPixels(5),
  },
  [PaperSize.BusinessCard]: {
    width: inchesToPixels(2),
    height: inchesToPixels(3.5),
  },
};
